import React, { useRef, useState } from 'react';

const fmt = (n) => new Intl.NumberFormat().format(Number(n || 0));

function fmtShort(v) {
    const n = Number(v) || 0;
    if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
    return String(Math.round(n));
}

// Round up to a "nice" axis maximum (1, 2, 5 × 10^k).
function niceCeil(x) {
    if (x <= 0) return 1;
    const e = Math.floor(Math.log10(x));
    const base = Math.pow(10, e);
    const f = x / base;
    const nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
    return nf * base;
}

// Vertical padding (in % of the plot box) so peaks/zeros aren't flush to the edges.
const TOP_PAD = 6;
const BOT_PAD = 12; // extra room for the x-axis date labels

// Multi-series line chart with graduated Y-axis, hover tooltip and a legend that
// doubles as a filter. The filter is what lets one chart carry the whole
// dashboard: the series share a unit (events per day) but not a magnitude, so
// visits would otherwise flatten signups into the axis. Hiding a series rescales
// the axis to what is left; colours stay bolted to their series, so a survivor
// never changes hue.
// series: [{ name, color, points: [{ date, value }] }]
export function LineChart({ series = [], height = 170, yTicks = 4, xTicks = 3, ariaLabel = '' }) {
    const ref = useRef(null);
    const [hover, setHover] = useState(null);
    const [hidden, setHidden] = useState(() => new Set());

    const base = series[0]?.points || [];
    const n = base.length;
    const shown = series.filter((s) => !hidden.has(s.name));

    const toggle = (name) => setHidden((prev) => {
        const next = new Set(prev);
        if (next.has(name)) next.delete(name);
        // Never hide the last one: an empty plot has nothing to say and no
        // obvious way back other than clicking every legend item again.
        else if (series.length - next.size > 1) next.add(name);
        return next;
    });

    if (!series.length || n === 0) {
        return <p className="admin-empty">—</p>;
    }

    const allVals = shown.flatMap((s) => s.points.map((p) => Number(p.value) || 0));
    const niceMax = niceCeil(Math.max(1, ...allVals));
    const plotH = 100 - TOP_PAD - BOT_PAD;

    const xPct = (i) => (n === 1 ? 50 : (i / (n - 1)) * 100);
    const yPct = (v) => TOP_PAD + (1 - Math.max(0, Number(v) || 0) / niceMax) * plotH;
    const poly = (pts) => pts.map((p, i) => `${xPct(i).toFixed(2)},${yPct(p.value).toFixed(2)}`).join(' ');

    const onMove = (e) => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const rel = (e.clientX - r.left) / r.width;
        const idx = Math.max(0, Math.min(n - 1, Math.round(rel * (n - 1))));
        setHover(idx);
    };

    const ticks = Array.from({ length: yTicks + 1 }, (_, t) => ({
        value: (niceMax * t) / yTicks,
        top: TOP_PAD + (1 - t / yTicks) * plotH,
    }));

    // Evenly spaced date labels: a chart spanning the full width needs more than
    // first/middle/last to place a peak on a day. Each label is pinned to its
    // own point rather than spread by the flexbox, which would drift from the
    // data as soon as the labels are more than two.
    const labelCount = Math.max(2, Math.min(xTicks, n));
    const xLabels = Array.from({ length: labelCount }, (_, i) => {
        const idx = Math.round((i / (labelCount - 1)) * (n - 1));
        return { date: base[idx]?.date || '', at: xPct(idx) };
    });
    const hx = hover != null ? xPct(hover) : 0;

    return (
        <div className="admin-chart-wrap">
            {series.length > 1 && (
                <div className="admin-legend">
                    {series.map((s) => {
                        const off = hidden.has(s.name);
                        return (
                            <button
                                type="button"
                                className={`admin-legend-item${off ? ' off' : ''}`}
                                key={s.name}
                                onClick={() => toggle(s.name)}
                                aria-pressed={!off}
                            >
                                <span className="admin-legend-dot" style={{ background: s.color }} />
                                {s.name}
                            </button>
                        );
                    })}
                </div>
            )}

            <div
                className="admin-chart-plot"
                ref={ref}
                style={{ height }}
                onMouseMove={onMove}
                onMouseLeave={() => setHover(null)}
            >
                {ticks.map((t) => (
                    <div className="admin-grid-line" style={{ top: `${t.top}%` }} key={t.value}>
                        <span className="admin-grid-label">{fmtShort(t.value)}</span>
                    </div>
                ))}

                <svg
                    className="admin-linechart"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                    role="img"
                    aria-label={ariaLabel || undefined}
                >
                    {shown.map((s) => (
                        <polyline
                            key={s.name}
                            points={poly(s.points)}
                            fill="none"
                            stroke={s.color}
                            strokeWidth="2"
                            strokeLinejoin="round"
                            vectorEffect="non-scaling-stroke"
                        />
                    ))}
                </svg>

                {hover != null && (
                    <>
                        <div className="admin-hover-line" style={{ left: `${hx}%` }} />
                        {shown.map((s) => (
                            <div
                                key={s.name}
                                className="admin-hover-dot"
                                style={{ left: `${hx}%`, top: `${yPct(s.points[hover]?.value)}%`, background: s.color }}
                            />
                        ))}
                        <div className={`admin-tooltip${hx > 60 ? ' left' : ''}`} style={{ left: `${hx}%` }}>
                            <div className="admin-tooltip-date">{base[hover]?.date}</div>
                            {shown.map((s) => (
                                <div className="admin-tooltip-row" key={s.name}>
                                    <span className="admin-legend-dot" style={{ background: s.color }} />
                                    {series.length > 1 ? `${s.name}: ` : ''}
                                    <strong>{fmt(s.points[hover]?.value)}</strong>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                <div className="admin-xaxis">
                    {xLabels.map((label, i) => (
                        <span
                            key={`${label.date}-${i}`}
                            style={{
                                left: `${label.at}%`,
                                // The end labels hug the plot edges instead of
                                // overflowing them.
                                transform: i === 0
                                    ? 'none'
                                    : i === labelCount - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
                            }}
                        >
                            {label.date.slice(5)}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
}

// Stage funnel: nested counts drawn as bars proportional to the first stage, so
// the drop between steps is visible instead of having to be inferred from six
// side-by-side cards. Each row after the first also shows its step conversion
// (share of the stage above it), which is the number that actually moves.
// stages: [{ name, value, color }]
export function Funnel({ stages = [] }) {
    const rows = stages.filter(Boolean);
    const top = Number(rows[0]?.value) || 0;
    if (!rows.length || top <= 0) {
        return <p className="admin-empty">—</p>;
    }

    return (
        <div className="admin-funnel">
            {rows.map((stage, i) => {
                const value = Number(stage.value) || 0;
                const prev = Number(rows[i - 1]?.value) || 0;
                const ofTop = value / top;
                const ofPrev = i === 0 || prev <= 0 ? null : value / prev;
                return (
                    <div className="admin-funnel-row" key={stage.name}>
                        <div className="admin-funnel-head">
                            <span className="admin-funnel-name">{stage.name}</span>
                            <span className="admin-funnel-value">
                                {fmt(value)}
                                <span className="admin-funnel-pct">{`${(ofTop * 100).toFixed(ofTop * 100 >= 10 ? 0 : 1)}%`}</span>
                            </span>
                        </div>
                        <div className="admin-funnel-track">
                            <div
                                className="admin-funnel-bar"
                                style={{ width: `${Math.max(ofTop * 100, value > 0 ? 1.5 : 0)}%`, background: stage.color }}
                            />
                        </div>
                        {ofPrev != null && (
                            <span className="admin-funnel-step">
                                {`→ ${(ofPrev * 100).toFixed(ofPrev * 100 >= 10 ? 0 : 1)}%`}
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
